import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Clock, 
  Activity, 
  FileText, 
  User, 
  Heart,
  ChevronLeft,
  ChevronRight,
  Filter,
  Eye,
  Grid,
  List,
  Search,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader,
  X,
  Calendar,
  ExternalLink,
  Edit3  // ← Agregado para las notas generales
} from 'lucide-react';

// Import the custom hook
import { useCalendarData } from './useCalendarData';

const MedicalCalendar = ({ patientId }) => {
  // Component states
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week, day
  const [displayMode, setDisplayMode] = useState('calendar'); // calendar, table
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showDayEvents, setShowDayEvents] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter states - CORREGIDO: usar 'generalNotes' en lugar de 'notes'
  const [activeFilters, setActiveFilters] = useState({
    treatmentDetails: true,
    laboratory: true,
    generalNotes: true,  // ← CAMBIO CRÍTICO: 'notes' → 'generalNotes'
    diagnosticImaging: true,
    medicalRecord: true,
    nursing: true,
    molecularMedicine: true
  });

  // Use custom hook to get data
  const {
    events,
    isLoading,
    error,
    lastUpdated,
    loadingProgress,
    refreshEvents,
    getEventsForDate,
    getEventsInRange,
    searchEvents,
    statistics,
    hasData,
    isEmpty
  } = useCalendarData(patientId, {
    autoRefresh: true,
    refreshInterval: 30000, // 30 seconds
    enableCache: true
  });

  // Event types configuration - CORREGIDO
  const eventTypes = {
    treatmentDetails: {
      label: 'Treatment Details',
      color: '#0d6efd',
      bgColor: '#e7f1ff',
      icon: <Activity size={16} />,
    },
    laboratory: {
      label: 'Laboratory',
      color: '#dc3545',
      bgColor: '#f8d7da',
      icon: <FileText size={16} />,
    },
    generalNotes: {  // ← CAMBIO CRÍTICO: 'notes' → 'generalNotes'
      label: 'General Notes',  // ← Mejorado el label
      color: '#198754',
      bgColor: '#d1e7dd',
      icon: <Edit3 size={16} />,  // ← Icono más apropiado para notas
    },
    diagnosticImaging: {
      label: 'Diagnostic Imaging',
      color: '#fd7e14',
      bgColor: '#fff3e0',
      icon: <Eye size={16} />,
    },
    medicalRecord: {
      label: 'Medical Record',
      color: '#6f42c1',
      bgColor: '#e2d9f3',
      icon: <User size={16} />,
    },
    nursing: {
      label: 'Nursing',
      color: '#20c997',
      bgColor: '#c3e6cb',
      icon: <Heart size={16} />,
    },
    molecularMedicine: {
      label: 'Molecular Medicine',
      color: '#6610f2',
      bgColor: '#e0cffc',
      icon: <Activity size={16} />,
    }
  };

  // Filter events according to active filters and search term
  const filteredEvents = useMemo(() => {
    // Debug: Verificar qué tipos de eventos tenemos
    console.log('🔍 Debug - Available event types in events:', [...new Set(events.map(e => e.type))]);
    console.log('🔍 Debug - Active filters:', activeFilters);
    console.log('🔍 Debug - Total events before filtering:', events.length);
    
    // First filter by active types
    let filtered = events.filter(event => {
      const isActive = activeFilters[event.type];
      if (!isActive) {
        console.log(`🚫 Filtering out event type: ${event.type}`);
      }
      return isActive;
    });
    
    console.log('🔍 Debug - Events after type filtering:', filtered.length);
    console.log('🔍 Debug - generalNotes events:', filtered.filter(e => e.type === 'generalNotes').length);
    
    // Then apply search if exists
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(event => 
        event.title?.toLowerCase().includes(term) ||
        event.description?.toLowerCase().includes(term) ||
        event.type?.toLowerCase().includes(term) ||
        event.eventSubtype?.toLowerCase().includes(term) ||
        event.doctor?.toLowerCase().includes(term) ||
        event.status?.toLowerCase().includes(term)
      );
    }

    console.log('🔍 Debug - Final filtered events:', filtered.length);
    return filtered;
  }, [events, activeFilters, searchTerm]);

  // Calendar navigation
  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    
    switch (viewMode) {
      case 'month':
        newDate.setMonth(currentDate.getMonth() + direction);
        break;
      case 'week':
        newDate.setDate(currentDate.getDate() + (direction * 7));
        break;
      case 'day':
        newDate.setDate(currentDate.getDate() + direction);
        break;
    }
    
    setCurrentDate(newDate);
  };

  // Generate calendar grid for month view
  const generateCalendarGrid = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const currentDay = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay));
      currentDay.setDate(currentDay.getDate() + 1);
    }
    
    return days;
  };

  // Generate week view days
  const generateWeekDays = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  // Get events for specific date using filtered events
  const getEventsForSpecificDate = (date) => {
    const dayEvents = filteredEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.toDateString() === date.toDateString();
    });
    
    // Debug para una fecha específica
    if (date.getDate() === 18) {
      console.log(`🔍 Debug - Events for ${date.toDateString()}:`, dayEvents);
      console.log(`🔍 Debug - generalNotes for this date:`, dayEvents.filter(e => e.type === 'generalNotes'));
    }
    
    return dayEvents;
  };

  // Handle day click - Open day events offcanvas
  const handleDayClick = (date) => {
    setSelectedDate(date);
    setShowDayEvents(true);
  };

  // Handle event click - Open event details offcanvas
  const handleEventClick = (event, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedEvent(event);
    setShowEventDetails(true);
  };

  // Toggle filters
  const toggleFilter = (filterKey) => {
    console.log(`🔄 Toggling filter: ${filterKey}`);
    setActiveFilters(prev => {
      const newFilters = {
        ...prev,
        [filterKey]: !prev[filterKey]
      };
      console.log('🔄 New filters:', newFilters);
      return newFilters;
    });
  };

  const calendarDays = generateCalendarGrid();
  const weekDays = generateWeekDays();
  const selectedDateEvents = getEventsForSpecificDate(selectedDate);

  // Render loading indicator
  const renderLoadingIndicator = () => {
    if (!isLoading) return null;
    
    return (
      <div className="d-flex align-items-center justify-content-center py-4">
        <Loader className="me-2" size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span className="text-muted">
          Loading medical events... {loadingProgress}%
        </span>
        {loadingProgress > 0 && (
          <div className="progress ms-2" style={{width: '100px', height: '4px'}}>
            <div 
              className="progress-bar" 
              style={{width: `${loadingProgress}%`}}
            ></div>
          </div>
        )}
      </div>
    );
  };

  // Render error message
  const renderError = () => {
    if (!error) return null;
    
    return (
      <div className="alert alert-danger d-flex align-items-center" role="alert">
        <AlertCircle className="me-2" size={20} />
        <div className="flex-grow-1">
          <strong>Error loading medical events:</strong>
          <div className="small">{error.message}</div>
        </div>
        <button 
          className="btn btn-outline-danger btn-sm"
          onClick={refreshEvents}
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    );
  };

  // Render statistics
  const renderStatistics = () => {
    if (!statistics || isEmpty) return null;
    
    return (
      <div className="row g-3 mb-3">
        <div className="col-sm-6 col-md-3">
          <div className="card text-center">
            <div className="card-body py-2">
              <h6 className="card-title mb-1">Total Events</h6>
              <h4 className="text-primary mb-0">{statistics.total || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-md-3">
          <div className="card text-center">
            <div className="card-body py-2">
              <h6 className="card-title mb-1">Today</h6>
              <h4 className="text-info mb-0">{statistics.today || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-md-3">
          <div className="card text-center">
            <div className="card-body py-2">
              <h6 className="card-title mb-1">This Week</h6>
              <h4 className="text-success mb-0">{statistics.thisWeek || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-sm-6 col-md-3">
          <div className="card text-center">
            <div className="card-body py-2">
              <h6 className="card-title mb-1">Overdue</h6>
              <h4 className="text-danger mb-0">{statistics.overdue || 0}</h4>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render week view
  const renderWeekView = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    
    return (
      <div className="week-view">
        <div className="row">
          {/* Time column */}
          <div className="col-1">
            <div style={{ height: '60px' }}></div> {/* Header spacer */}
            {hours.map(hour => (
              <div key={hour} className="border-bottom text-center py-2 text-muted small" style={{ height: '60px' }}>
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>
          
          {/* Days columns */}
          {weekDays.map(day => {
            const dayEvents = getEventsForSpecificDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            
            return (
              <div key={day.toISOString()} className="col">
                {/* Day header */}
                <div 
                  className={`text-center p-2 border-bottom ${isToday ? 'bg-primary text-white' : 'bg-light'}`}
                  style={{ height: '60px', cursor: 'pointer' }}
                  onClick={() => handleDayClick(day)}
                >
                  <div className="fw-bold">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                  <div className={isToday ? 'text-white' : 'text-muted'}>{day.getDate()}</div>
                </div>
                
                {/* Hour slots */}
                <div className="position-relative">
                  {hours.map(hour => (
                    <div key={hour} className="border-bottom" style={{ height: '60px' }}>
                      {/* Events for this hour */}
                      {dayEvents
                        .filter(event => new Date(event.date).getHours() === hour)
                        .map(event => {
                          const eventType = eventTypes[event.type];
                          return (
                            <div
                              key={event.id}
                              className="position-absolute rounded p-1 m-1 text-truncate"
                              style={{
                                backgroundColor: eventType?.bgColor || '#f8f9fa',
                                color: eventType?.color || '#6c757d',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                width: 'calc(100% - 8px)',
                                zIndex: 2
                              }}
                              onClick={(e) => handleEventClick(event, e)}
                            >
                              <div className="d-flex align-items-center gap-1">
                                {React.cloneElement(eventType?.icon || <FileText size={10} />, { size: 10 })}
                                <span className="text-truncate">{event.title}</span>
                              </div>
                              <div className="text-truncate" style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                                {new Date(event.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!patientId) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <AlertCircle className="text-muted mb-3" size={48} />
          <h5 className="text-muted">No Patient Selected</h5>
          <p className="text-muted">Please select a patient to view their medical calendar.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <Calendar size={20} />
                Medical Calendar
                {lastUpdated && (
                  <small className="text-muted">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </small>
                )}
              </h5>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-end gap-2">
                {/* Debug button para verificar eventos */}
                <button
                  className="btn btn-outline-info btn-sm"
                  onClick={() => {
                    console.log('🐛 DEBUG INFO:');
                    console.log('Total events:', events.length);
                    console.log('Filtered events:', filteredEvents.length);
                    console.log('Event types:', [...new Set(events.map(e => e.type))]);
                    console.log('generalNotes events:', events.filter(e => e.type === 'generalNotes'));
                    console.log('Active filters:', activeFilters);
                  }}
                  title="Debug: Log events to console"
                >
                  🐛
                </button>

                {/* Refresh button */}
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={refreshEvents}
                  disabled={isLoading}
                >
                  <RefreshCw size={14} />
                </button>

                {/* Search */}
                <div className="input-group" style={{maxWidth: '250px'}}>
                  <span className="input-group-text">
                    <Search size={14} />
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                {/* Filters */}
                <button
                  className={`btn btn-sm ${showFilters ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={14} />
                </button>
                
                {/* View toggle */}
                <div className="btn-group btn-group-sm">
                  <button
                    className={`btn ${displayMode === 'calendar' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setDisplayMode('calendar')}
                  >
                    <Grid size={14} />
                  </button>
                  <button
                    className={`btn ${displayMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setDisplayMode('table')}
                  >
                    <List size={14} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible filters */}
        {showFilters && (
          <div className="card-body border-bottom">
            <div className="d-flex flex-wrap gap-2">
              {Object.entries(eventTypes).map(([key, config]) => (
                <label key={key} className="form-check form-check-inline mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={activeFilters[key] || false}
                    onChange={() => toggleFilter(key)}
                  />
                  <span 
                    className="form-check-label badge d-flex align-items-center gap-1"
                    style={{ 
                      backgroundColor: activeFilters[key] ? config.color : '#f8f9fa',
                      color: activeFilters[key] ? 'white' : '#6c757d',
                      cursor: 'pointer'
                    }}
                  >
                    {config.icon}
                    {config.label}
                    {/* Mostrar contador de eventos por tipo */}
                    <span className="ms-1 small">
                      ({events.filter(e => e.type === key).length})
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Statistics */}
        {renderStatistics()}

        {/* Error and loading indicators */}
        {renderError()}
        {renderLoadingIndicator()}

        {/* Main content */}
        {displayMode === 'calendar' ? (
          <div className="card-body">
            {/* Calendar header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigateCalendar(-1)}
                  disabled={isLoading}
                >
                  <ChevronLeft size={16} />
                </button>
                <h4 className="mb-0">
                  {viewMode === 'month' && currentDate.toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                  {viewMode === 'week' && `Week of ${weekDays[0]?.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  })}`}
                </h4>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigateCalendar(1)}
                  disabled={isLoading}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              
              <div className="btn-group btn-group-sm">
                <button
                  className={`btn ${viewMode === 'month' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('month')}
                >
                  Month
                </button>
                <button
                  className={`btn ${viewMode === 'week' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('week')}
                >
                  Week
                </button>
              </div>
            </div>

            {/* Calendar content */}
            {viewMode === 'month' ? (
              <div className="calendar-grid">
                {/* Day headers */}
                <div className="row text-center fw-bold text-muted mb-2">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                    <div key={day} className="col p-2">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar days with fixed grid */}
                <div 
                  style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(7, 1fr)', 
                    gap: '1px',
                    backgroundColor: '#dee2e6' // Border color between cells
                  }}
                >
                  {calendarDays.map((day, index) => {
                    const dayEvents = getEventsForSpecificDate(day);
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isSelected = day.toDateString() === selectedDate.toDateString();
                    
                    return (
                      <div
                        key={index}
                        className={`p-2 position-relative d-flex flex-column ${
                          isCurrentMonth ? 'bg-white' : 'bg-light text-muted'
                        } ${isToday ? 'bg-primary bg-opacity-10' : ''} ${
                          isSelected ? 'bg-info bg-opacity-20' : ''
                        }`}
                        style={{ 
                          height: '120px', // Fixed height for all cells
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          overflow: 'hidden'
                        }}
                        onClick={() => handleDayClick(day)}
                        onMouseEnter={(e) => {
                          if (!isSelected && !isToday) {
                            e.target.style.backgroundColor = '#f8f9fa';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected && !isToday) {
                            e.target.style.backgroundColor = isCurrentMonth ? 'white' : '#f8f9fa';
                          }
                        }}
                      >
                        {/* Day number and event count */}
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <span className={`fw-bold ${isToday ? 'text-primary' : ''} ${isSelected ? 'text-info' : ''}`}>
                            {day.getDate()}
                          </span>
                          {dayEvents.length > 0 && (
                            <span className="badge bg-primary rounded-pill" style={{fontSize: '0.6rem'}}>
                              {dayEvents.length}
                            </span>
                          )}
                        </div>
                        
                        {/* Event indicators - only show dots for clean look */}
                        <div className="flex-grow-1 d-flex flex-column gap-1">
                          {dayEvents.slice(0, 3).map(event => {
                            const eventType = eventTypes[event.type];
                            return (
                              <div
                                key={event.id}
                                className="rounded px-1 py-0 d-flex align-items-center"
                                style={{
                                  backgroundColor: eventType?.bgColor || '#f8f9fa',
                                  color: eventType?.color || '#6c757d',
                                  fontSize: '0.65rem',
                                  lineHeight: '1.2',
                                  height: '18px',
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => handleEventClick(event, e)}
                              >
                                <div className="d-flex align-items-center gap-1 text-truncate">
                                  <div 
                                    className="rounded-circle"
                                    style={{
                                      width: '6px',
                                      height: '6px',
                                      backgroundColor: eventType?.color || '#6c757d',
                                      flexShrink: 0
                                    }}
                                  />
                                  <span className="text-truncate">{event.title}</span>
                                </div>
                              </div>
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <div className="text-center" style={{fontSize: '0.6rem', color: '#6c757d'}}>
                              +{dayEvents.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Week view */
              <div style={{ overflowX: 'auto' }}>
                {renderWeekView()}
              </div>
            )}
          </div>
        ) : (
          /* Table view */
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Event</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.slice(0, 50).map(event => {
                    const eventType = eventTypes[event.type];
                    return (
                      <tr key={event.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Clock size={14} className="text-muted" />
                            <div>
                              {event.date instanceof Date ? event.date.toLocaleDateString() : 'N/A'}
                              <br />
                              <small className="text-muted">
                                {event.date instanceof Date ? 
                                  event.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                  'N/A'
                                }
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span 
                            className="badge d-flex align-items-center gap-1"
                            style={{ 
                              backgroundColor: eventType?.bgColor || '#f8f9fa',
                              color: eventType?.color || '#6c757d',
                              maxWidth: 'fit-content'
                            }}
                          >
                            {eventType?.icon}
                            {eventType?.label || event.type}
                          </span>
                        </td>
                        <td>
                          <strong>{event.title}</strong>
                          <br />
                          <small className="text-muted">
                            {event.eventSubtype || event.dateType?.replace('_', ' ').toUpperCase()}
                          </small>
                        </td>
                        <td>
                          <span className="text-truncate d-block" style={{maxWidth: '200px'}}>
                            {event.description || '-'}
                          </span>
                        </td>
                        <td>
                          <span 
                            className={`badge ${
                              event.status === 'completed' ? 'bg-success' :
                              event.status === 'pending' ? 'bg-warning' :
                              event.status === 'active' ? 'bg-primary' :
                              'bg-secondary'
                            }`}
                          >
                            {event.status || 'Unknown'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEventClick(event)}
                          >
                            <Eye size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {filteredEvents.length === 0 && !isLoading && !error && (
                <div className="text-center py-5">
                  <Calendar className="text-muted mb-3" size={48} />
                  <h6 className="text-muted">No medical events found</h6>
                  <p className="text-muted">
                    {searchTerm || Object.values(activeFilters).some(v => !v) 
                      ? 'Try adjusting your search or filters'
                      : 'No medical events available for this patient'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Day Events Offcanvas */}
      <div 
        className={`offcanvas offcanvas-end ${showDayEvents ? 'show' : ''}`}
        tabIndex="-1"
        style={{ 
          visibility: showDayEvents ? 'visible' : 'hidden',
          width: '400px'
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title d-flex align-items-center gap-2">
            <Calendar size={20} />
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={() => setShowDayEvents(false)}
          ></button>
        </div>
        
        <div className="offcanvas-body p-0">
          {selectedDateEvents.length === 0 ? (
            <div className="text-center p-4 h-100 d-flex flex-column justify-content-center align-items-center">
              <Calendar className="text-muted mb-3" size={48} />
              <h6 className="text-muted mb-2">No events for this date</h6>
              <p className="text-muted small">There are no medical events scheduled for this day.</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {selectedDateEvents.map(event => {
                const eventType = eventTypes[event.type];
                return (
                  <div
                    key={event.id}
                    className="list-group-item list-group-item-action border-0"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="d-flex align-items-start gap-3">
                      <div 
                        className="rounded p-2 mt-1"
                        style={{ 
                          backgroundColor: eventType?.bgColor || '#f8f9fa',
                          color: eventType?.color || '#6c757d'
                        }}
                      >
                        {eventType?.icon}
                      </div>
                      
                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <h6 className="mb-1 text-truncate fw-bold">
                            {event.title}
                          </h6>
                          <small className="text-muted ms-2">
                            {event.date instanceof Date ? 
                              event.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                              'N/A'
                            }
                          </small>
                        </div>
                        
                        <div className="mb-2">
                          <span 
                            className="badge me-2"
                            style={{ 
                              backgroundColor: eventType?.color || '#6c757d',
                              color: 'white'
                            }}
                          >
                            {eventType?.label || event.type}
                          </span>
                          {event.status && (
                            <span 
                              className={`badge ${
                                event.status === 'completed' ? 'bg-success' :
                                event.status === 'pending' ? 'bg-warning text-dark' :
                                event.status === 'active' ? 'bg-primary' :
                                'bg-secondary'
                              }`}
                            >
                              {event.status}
                            </span>
                          )}
                        </div>
                        
                        {event.description && (
                          <p className="mb-2 text-muted small">
                            {event.description}
                          </p>
                        )}
                        
                        {event.eventSubtype && (
                          <small className="text-muted">
                            {event.eventSubtype}
                          </small>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Event Details Offcanvas */}
      <div 
        className={`offcanvas offcanvas-end ${showEventDetails ? 'show' : ''}`}
        tabIndex="-1"
        style={{ 
          visibility: showEventDetails ? 'visible' : 'hidden',
          width: '500px'
        }}
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title d-flex align-items-center gap-2">
            {selectedEvent && eventTypes[selectedEvent.type]?.icon}
            Event Details
          </h5>
          <button 
            type="button" 
            className="btn-close"
            onClick={() => setShowEventDetails(false)}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          {selectedEvent && (
            <div className="h-100 d-flex flex-column">
              {/* Event header */}
              <div className="mb-4">
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div 
                    className="rounded p-3"
                    style={{ 
                      backgroundColor: eventTypes[selectedEvent.type]?.bgColor || '#f8f9fa',
                      color: eventTypes[selectedEvent.type]?.color || '#6c757d'
                    }}
                  >
                    {React.cloneElement(eventTypes[selectedEvent.type]?.icon || <FileText />, { size: 24 })}
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="mb-1">{selectedEvent.title}</h4>
                    <p className="text-muted mb-0">
                      {eventTypes[selectedEvent.type]?.label || selectedEvent.type}
                    </p>
                  </div>
                </div>
                
                <div className="row g-3">
                  <div className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <Calendar size={16} className="text-muted" />
                      <div>
                        <small className="text-muted d-block">Date</small>
                        <span>
                          {selectedEvent.date instanceof Date ? 
                            selectedEvent.date.toLocaleDateString() : 
                            'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <Clock size={16} className="text-muted" />
                      <div>
                        <small className="text-muted d-block">Time</small>
                        <span>
                          {selectedEvent.date instanceof Date ? 
                            selectedEvent.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                            'N/A'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Event details */}
              <div className="flex-grow-1">
                <div className="card border-0 bg-light mb-3">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label text-muted small mb-1">Status</label>
                        <div>
                          <span 
                            className={`badge ${
                              selectedEvent.status === 'completed' ? 'bg-success' :
                              selectedEvent.status === 'pending' ? 'bg-warning text-dark' :
                              selectedEvent.status === 'active' ? 'bg-primary' :
                              'bg-secondary'
                            }`}
                          >
                            {selectedEvent.status || 'Unknown'}
                          </span>
                        </div>
                      </div>
                      
                      {selectedEvent.eventSubtype && (
                        <div className="col-12">
                          <label className="form-label text-muted small mb-1">Event Subtype</label>
                          <div>{selectedEvent.eventSubtype}</div>
                        </div>
                      )}
                      
                      {selectedEvent.description && (
                        <div className="col-12">
                          <label className="form-label text-muted small mb-1">Description</label>
                          <div>{selectedEvent.description}</div>
                        </div>
                      )}
                      
                      {selectedEvent.doctor && (
                        <div className="col-12">
                          <label className="form-label text-muted small mb-1">Doctor</label>
                          <div className="d-flex align-items-center gap-2">
                            <User size={16} className="text-muted" />
                            <span>{selectedEvent.doctor}</span>
                          </div>
                        </div>
                      )}
                      
                      {selectedEvent.dateType && (
                        <div className="col-12">
                          <label className="form-label text-muted small mb-1">Date Type</label>
                          <div>{selectedEvent.dateType.replace('_', ' ').toUpperCase()}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Additional event information */}
                {selectedEvent.id && (
                  <div className="mt-3">
                    <small className="text-muted">Event ID: {selectedEvent.id}</small>
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="border-top pt-3 mt-3">
                <div className="d-flex gap-2">                  
                  <button 
                    className="btn btn-outline-secondary"
                    onClick={() => setShowEventDetails(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backdrop for offcanvas */}
      {(showDayEvents || showEventDetails) && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={() => {
            setShowDayEvents(false);
            setShowEventDetails(false);
          }}
        ></div>
      )}
    </>
  );
};

export default MedicalCalendar;